import { useCallback, useEffect, useMemo, useState } from "react";
import { FiEdit2, FiMapPin, FiPlus, FiSearch, FiX } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

const emptyForm = {
  code: "",
  name: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  country: "IN",
  isActive: true,
};

export default function BranchesPage() {
  const { accessToken } = useAuth();
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadBranches = useCallback(async () => {
    try {
      const response = await apiRequest("/branches", { accessToken });
      setBranches(Array.isArray(response) ? response : response?.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load branches.");
    }
  }, [accessToken]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const filteredBranches = useMemo(() => {
    const query = search.toLowerCase();
    return branches.filter((branch) =>
      [branch.name, branch.code, branch.city, branch.state].some((value) =>
        value?.toLowerCase().includes(query),
      ),
    );
  }, [branches, search]);
  const branchesPagination = useClientPagination(filteredBranches);

  const openModal = (branch = null) => {
    setEditing(branch);
    setForm(
      branch
        ? {
            code: branch.code || "",
            name: branch.name || "",
            email: branch.email || "",
            phone: branch.phone || "",
            city: branch.city || "",
            state: branch.state || "",
            country: branch.country || "IN",
            isActive: branch.isActive ?? true,
          }
        : emptyForm,
    );
    setModalOpen(true);
  };

  const saveBranch = async (event) => {
    event.preventDefault();
    setError("");

    try {
      await apiRequest(editing ? `/branches/${editing.id}` : "/branches", {
        method: editing ? "PATCH" : "POST",
        accessToken,
        body: {
          ...form,
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
        },
      });

      setModalOpen(false);
      await loadBranches();
    } catch (requestError) {
      setError(requestError.message || "Unable to save branch.");
    }
  };

  const deactivate = async (branch) => {
    if (!window.confirm(`Deactivate "${branch.name}"?`)) return;

    try {
      await apiRequest(`/branches/${branch.id}/deactivate`, {
        method: "POST",
        accessToken,
      });
      await loadBranches();
    } catch (requestError) {
      setError(requestError.message || "Unable to deactivate branch.");
    }
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Administration / Branches</p>
          <h1 className="mt-1 text-3xl font-bold">Branches</h1>
          <p className="mt-2 text-gray-500">Manage company branches and locations.</p>
        </div>
        <button onClick={() => openModal()} className="flex h-11 items-center gap-2 rounded-xl bg-primary-800 px-5 text-white">
          <FiPlus /> Add Branch
        </button>
      </div>

      {error && <div className="mb-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="overflow-hidden rounded-3xl border bg-white">
        <div className="border-b p-5">
          <div className="relative max-w-md">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search branches..." className="h-12 w-full rounded-xl border pl-12 pr-4" />
          </div>
        </div>

        <table className="w-full min-w-[850px]">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-6 py-4">Branch</th>
              <th className="px-6 py-4">Code</th>
              <th className="px-6 py-4">Location</th>
              <th className="px-6 py-4">Stores</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {branchesPagination.pageItems.map((branch) => (
              <tr key={branch.id} className="border-t hover:bg-gray-50">
                <td className="px-6 py-4 font-semibold">{branch.name}</td>
                <td className="px-6 py-4 font-mono text-sm">{branch.code}</td>
                <td className="px-6 py-4 text-sm">{[branch.city, branch.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-6 py-4">{branch._count?.stores ?? 0}</td>
                <td className="px-6 py-4">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${branch.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                    {branch.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openModal(branch)} className="flex h-9 w-9 items-center justify-center rounded-lg border"><FiEdit2 /></button>
                    {branch.isActive && <button onClick={() => deactivate(branch)} className="rounded-lg border px-3 text-xs text-red-600">Deactivate</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ClientPagination pagination={branchesPagination} label="branches" />
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form onSubmit={saveBranch} className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-8 py-6">
              <h2 className="text-2xl font-bold">{editing ? "Edit Branch" : "Add Branch"}</h2>
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-xl bg-gray-100 p-3"><FiX /></button>
            </div>

            <div className="grid gap-5 p-8 md:grid-cols-2">
              {[
                ["name", "Branch Name", "Mumbai Andheri"],
                ["code", "Branch Code", "MUM-AND"],
                ["email", "Email", "andheri@charms.com"],
                ["phone", "Phone", "9876543210"],
                ["city", "City", "Mumbai"],
                ["state", "State", "Maharashtra"],
              ].map(([field, label, placeholder]) => (
                <div key={field}>
                  <label className="mb-2 block text-sm font-medium">{label}{["name", "code"].includes(field) ? " *" : ""}</label>
                  <input
                    required={["name", "code"].includes(field)}
                    value={form[field]}
                    placeholder={placeholder}
                    onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                    className="h-12 w-full rounded-xl border px-4"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 border-t px-8 py-5">
              <button type="button" onClick={() => setModalOpen(false)} className="h-11 rounded-xl border px-6">Cancel</button>
              <button type="submit" className="h-11 rounded-xl bg-primary-800 px-6 text-white">Save Branch</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
